import React, { useState } from "react";
import { uploadCompanyLogo } from "../../../api/services/hroneApi";
import "./index.css";

export function EmailSignature({ signature, onChange, onFieldFocus, onLogoChange, companyLogo, errors = {} }) {
  const [logoError, setLogoError] = useState("");
  const [uploading, setUploading] = useState(false);

  const handleChange = (field, value) => {
    onChange({ ...signature, [field]: value });
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    setLogoError("");
    if (!file) return;

    const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
    const allowedExts = ["jpg", "jpeg", "png"];
    const ext = file.name.split(".").pop().toLowerCase();
    if (!allowedTypes.includes(file.type) || !allowedExts.includes(ext)) {
      setLogoError("Only image files are acceptable (jpg, jpeg, png).");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);
    const boundary = `Boundary-${Math.random().toString(16).substring(2)}`;

    try {
      setUploading(true);
      const response = await uploadCompanyLogo(formData, boundary);
      if (response.data?.status && response.data?.company_logo) {
        if (onLogoChange) {
          onLogoChange(response.data.company_logo);
        }
      }
    } catch (error) {
      console.error("Failed to upload logo:", error);
    } finally {
      setUploading(false);
    }
  };

  const removeLogo = () => {
    if (onLogoChange) {
      onLogoChange(null);
    }
  };

  return (
    <div className="card shadow-sm">
      {/* Header */}
      <div className="card-header d-flex justify-content-between align-items-center">
        <h6 className="mb-0 d-flex align-items-center gap-2">
          <i className="bi bi-pen"></i>
          <strong>Email Signature</strong>
        </h6>
      </div>

      {/* Body */}
      <div className="card-body">
        <>
          {/* Personal Info */}
          <div className="row g-2 mb-3">
            <div className="col-md-6">
              <label className="form-label">
                Full Name <span className="text-danger">*</span>
              </label>
              <input
                className={`form-control ${errors.signatureName ? "is-invalid" : ""}`}
                value={signature?.name}
                onChange={(e) => handleChange("name", e.target.value)}
                onFocus={() => onFieldFocus && onFieldFocus('signature-name')}
              />
              {errors.signatureName && (
                <div className="invalid-feedback">{errors.signatureName}</div>
              )}
            </div>
            <div className="col-md-6">
              <label className="form-label">
                Job Title <span className="text-danger">*</span>
              </label>
              <input
                className={`form-control ${errors.signatureTitle ? "is-invalid" : ""}`}
                value={signature?.title}
                onChange={(e) => handleChange("title", e.target.value)}
                onFocus={() => onFieldFocus && onFieldFocus('signature-title')}
              />
              {errors.signatureTitle && (
                <div className="invalid-feedback">{errors.signatureTitle}</div>
              )}
            </div>
          </div>

          {/* Contact Info */}
          <div className="row g-2 mb-3">
            <div className="col-md-6">
              <label className="form-label">
                Email <span className="text-danger">*</span>
              </label>
              <input
                className={`form-control ${errors.signatureEmail ? "is-invalid" : ""}`}
                value={signature?.email}
                onChange={(e) => handleChange("email", e.target.value)}
                onFocus={() => onFieldFocus && onFieldFocus('signature-email')}
              />
              {errors.signatureEmail && (
                <div className="invalid-feedback">{errors.signatureEmail}</div>
              )}
            </div>
            <div className="col-md-6">
              <label className="form-label">
                Phone <span className="text-danger">*</span>
              </label>
              <input
                className={`form-control ${errors.signaturePhone ? "is-invalid" : ""}`}
                value={signature?.phone}
                onChange={(e) => handleChange("phone", e.target.value)}
                onFocus={() => onFieldFocus && onFieldFocus('signature-phone')}
              />
              {errors.signaturePhone && (
                <div className="invalid-feedback">{errors.signaturePhone}</div>
              )}
            </div>
          </div>

          <div className="row g-2 mb-3">
            <div className="col-md-6">
              <label className="form-label">
                Website <span className="text-danger">*</span>
              </label>
              <input
                className={`form-control ${errors.signatureWebsite ? "is-invalid" : ""}`}
                value={signature?.website}
                onChange={(e) => handleChange("website", e.target.value)}
                onFocus={() => onFieldFocus && onFieldFocus('signature-website')}
              />
              {errors.signatureWebsite && (
                <div className="invalid-feedback">{errors.signatureWebsite}</div>
              )}
            </div>
            <div className="col-md-6">
              <label className="form-label">Company Logo <span className="text-danger">*</span></label>
              {companyLogo ? (
                <div className="logo-box">
                  <img src={companyLogo} alt="Logo" />
                  <button
                    className="btn btn-sm btn-outline-danger"
                    onClick={removeLogo}
                  >
                    Remove
                  </button>
                </div>
              ) : (
                <>
                  <div className="position-relative">
                    <input
                      type="file"
                      className={`form-control form-control-sm ${errors.signatureLogo || logoError ? "is-invalid" : ""}`}
                      accept="image/*"
                      onChange={handleImageUpload}
                      disabled={uploading}
                    />
                    {uploading && (
                      <span className="position-absolute top-50 end-0 translate-middle-y me-2">
                        <span className="spinner-border spinner-border-sm text-secondary" role="status" />
                      </span>
                    )}
                  </div>
                  {(errors.signatureLogo || logoError) && (
                    <div className="invalid-feedback d-block">{logoError || errors.signatureLogo}</div>
                  )}
                </>
              )}
            </div>
          </div>
        </>
      </div>
    </div>
  );
}

export function generateSignatureHtml(signature = {}) {
  const logoHtml = signature.logoUrl
    ? `<img src="${signature.logoUrl}" alt="Company Logo" style="height: 50px; width: auto; margin: 12px 0;" />`
    : "";

  const phoneHtml = "";

  const addressHtml = signature.address
    ? `<p style="= font-size: 13px; margin: 4px 0;">${signature.address}</p>`
    : "";

  const socialHtml =
    signature.linkedin || signature.twitter
      ? `<p style="font-size: 13px; margin: 8px 0;">
        ${
          signature.linkedin
            ? `<a href="https://${signature.linkedin}" style="text-decoration: none; margin-right: 12px;">LinkedIn</a>`
            : ""
        }
        ${
          signature.twitter
            ? `<a href="https://${signature.twitter}" style=" text-decoration: none;">Twitter</a>`
            : ""
        }
      </p>`
      : "";

  const taglineHtml = signature.tagline
    ? `<p style="font-size: 12px; font-style: italic;  margin-top: 12px; padding-top: 8px; border-top: 1px solid #e5e7eb;">"${signature.tagline}"</p>`
    : "";

  const nameHtml = signature.name ? `<p style="font-weight: 600; font-size: 16px; margin: 0;">${signature.name}</p>` : "";
  
  const titleCompanyHtml = signature.title || signature.phone
    ? `<p style="font-size: 14px; margin: 4px 0;">${
        signature.title || ""
      }${signature.title && signature.phone ? " | " : ""}${signature.phone || ""}</p>`
    : "";

  const contactHtml = signature.email || signature.website
    ? `<p style="font-size: 13px; margin: 4px 0;">
    ${
      signature.email
        ? `<a href="mailto:${signature.email}" >${signature.email}</a>`
        : ""
    }
    ${signature.email && signature.website ? " | " : ""}
    ${
      signature.website
        ? `<a href="https://${signature.website}" >${signature.website}</a>`
        : ""
    }
  </p>`
    : "";

  return `
<div style="margin-top: 24px; padding-top: 16px; border-top: 2px solid #e5e7eb;">
  <p style=" margin-bottom: 8px;">Thanks & Regards,</p>
  ${nameHtml}
  ${titleCompanyHtml}
  ${logoHtml}
  ${phoneHtml}
  ${contactHtml}
  ${addressHtml}
  ${socialHtml}
  ${taglineHtml}
</div>`;
}
